import React, { useState, useEffect, useReducer, useRef } from "react";

import { isSameDay, isYesterday, parseISO, format } from "date-fns";
import { es } from "date-fns/locale";
import socket from "../../services/socket-io";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  Comment,
  ArrowDownward,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";
import whatsBackgroundDark from "../../assets/wa-background-dark2.jpg";
import { useHistory } from 'react-router-dom';
import { Tooltip } from '@material-ui/core';
import NotesDrawer from '../NotesDrawer';
import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio/index.jsx";
import DocumentMessage from "../DocumentMessage";
import { hashDiagnosticId, recordDiagnosticEvent, useDiagnosticRender } from "../../services/frontendDiagnostics";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },

  messagesList: {
    backgroundImage:
      theme.palette.type === "dark"
        ? `url(${whatsBackgroundDark})`
        : `url(${whatsBackground})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px",
    overflowY: "scroll",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: "90px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.type === "dark" ? "#3D3C3F" : "#ffffff",
    color: theme.palette.type === "dark" ? "#e0e0e0" : "#303030",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    padding: 5,
    boxShadow: "0px 7px 20px -3px rgba(0,0,0,0.5)",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.palette.type === "dark" ? "#3a3a3a" : "#f0f0f0",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
    '&:hover': {
      backgroundColor: theme.palette.type === "dark" ? "#4a4a4a" : "#e0e0e0",
    },
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.type === "dark" ? "#025E4A" : "#dcf8c6",
    color: theme.palette.type === "dark" ? "#e0e0e0" : "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    padding: 5,
    boxShadow: "0px 7px 20px -3px rgba(0,0,0,0.5)",
  },
  
   messageNote: {
    alignSelf: "center",
    backgroundColor: theme.palette.type === "dark" ? "#4a4635" : "#fffbe6",
    color: theme.palette.type === "dark" ? "#e0e0e0" : "#8a6d00",
    padding: "10px 15px",
    borderRadius: "10px",
    maxWidth: "60%",
    margin: "10px 0",
    boxShadow: "0 1px 1px 0 rgba(0,0,0,.05)",
    position: "relative",
    whiteSpace: "pre-wrap",
  },

  noteTitle: {
    display: "block",
    fontWeight: "bold",
    fontSize: "0.8rem",
    color: theme.palette.type === "dark" ? "#e0e0e0" : "#6f5a00",
    marginBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.1)"}`,
    paddingBottom: theme.spacing(0.5),
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.palette.type === "dark" ? "#043D36" : "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
    '&:hover': {
      backgroundColor: theme.palette.type === "dark" ? "#064F46" : "#c0d9aa",
    },
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
    "& a": {
      color: theme.palette.type === "dark" ? "#4ea6ff" : "#065fd4",
      textDecoration: "underline",
    },
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.38)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
    "& a": {
      color: theme.palette.type === "dark" ? "#4ea6ff" : "#065fd4",
      textDecoration: "underline",
    },
  },

  noteContentItem: {
    overflowWrap: "break-word",
    padding: "6px",
    paddingBottom: "20px",
  },
  
  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: theme.palette.type === "dark" ? "#3c3c3c" : "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0px 7px 20px -3px rgba(0,0,0,0.5)",
  },

  dailyTimestampText: {
    color: theme.palette.type === "dark" ? "#aaa" : "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    color: "#999",
    verticalAlign: "middle",
    marginRight: 0,
    padding: 0,
  },

  deletedText: {
    fontSize: 14,
    color: "#999",
    verticalAlign: "middle",
  },

  ackDoneAllIcon: {
    color: "#34B7F1",
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(1),
  },

  documentPreview: {
    textAlign: "center",
    backgroundColor: theme.palette.type === "dark" ? "#444" : "#f9f9f9",
    border: "1px solid",
    borderColor: theme.palette.type === "dark" ? "#555" : "#ccc",
    borderRadius: 8,
    padding: theme.spacing(2),
    maxWidth: 300,
    color: theme.palette.type === "dark" ? "#eee" : "#333",
  },

  fileIcon: {
    fontSize: "2rem",
  },

  fileName: {
    marginTop: 8,
    fontWeight: "bold",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  fileType: {
    fontSize: "0.9rem",
    color: theme.palette.type === "dark" ? "#aaa" : "#666",
  },

  downloadButton: {
    marginTop: theme.spacing(1.5),
  },

  notesButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
  },
  
  scrollToBottomButton: {
    position: 'absolute',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 10,
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #ccc',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    }
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const messagesById = new Map(state.map((message) => [message.id, message]));
    const newMessages = messages.filter((message) => !messagesById.has(message.id));
    const updatedState = state.map((message) => messagesById.get(message.id) || message);

    messages.forEach((message) => {
      messagesById.set(message.id, message);
    });

    return [...newMessages, ...updatedState.map((message) => messagesById.get(message.id))];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((message) => message.id === newMessage.id);

    if (messageIndex !== -1) {
      return state.map((message) => (
        message.id === newMessage.id ? newMessage : message
      ));
    }

    return [...state, newMessage];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    return state.map((message) => (
      message.id === messageToUpdate.id ? messageToUpdate : message
    ));
  }

  if (action.type === "RESET") {
    return [];
  }
};

const MessagesList = ({ ticketId, isGroup, isPreview = false, highlightedMessageId }) => {
  const classes = useStyles();
  const history = useHistory();
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();
  const listRef = useRef();
  const isHighlightedScrolled = useRef(false);
  const prevScrollHeight = useRef(0);
  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);
  const findMessageAttempts = useRef(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notesCount, setNotesCount] = useState(0);
  const shouldScrollRef = useRef(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const scrollDebounce = useRef(null);
  useDiagnosticRender("MessagesList", () => ({
    ticketIdHash: hashDiagnosticId(ticketId),
    counts: {
      messagesRendered: messagesList.length,
      pageNumber,
      hasMore: hasMore ? 1 : 0,
      loading: loading ? 1 : 0,
      notesCount
    },
    metadata: {
      isPreview: isPreview ? "1" : "0",
      highlighted: highlightedMessageId ? "1" : "0"
    }
  }));
  
  const checkNotes = React.useCallback(async () => {
    if (!ticketId) return;
    try {
      const { data } = await api.get(`/notes/${ticketId}`);
      setNotesCount(data ? data.length : 0);
    } catch (err) {
      console.error("Error checking notes:", err);
    }
  }, [ticketId]);
  
  useEffect(() => {
    const preloadImages = [whatsBackground, whatsBackgroundDark];
    preloadImages.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    findMessageAttempts.current = 0;
    currentTicketId.current = ticketId;
    
    checkNotes();
  }, [ticketId, checkNotes]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          if (pageNumber > 1 && listRef.current) {
            prevScrollHeight.current = listRef.current.scrollHeight;
            shouldScrollRef.current = true;
          }

          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber, isPreview },
          });

          if (currentTicketId.current === ticketId) {
            if (pageNumber > 1 && listRef.current) {
                prevScrollHeight.current = listRef.current.scrollHeight;
                shouldScrollRef.current = true;
            }
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
          }

          if (pageNumber === 1 && data.messages.length > 1 && !highlightedMessageId) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId, isPreview]);

  useEffect(() => {
    const joinRoom = () => {
      const token = localStorage.getItem("token");

      socket.emit("joinChatBox", ticketId);
    };

    socket.on("connect", joinRoom);

    if (socket.connected) {
      joinRoom();
    }

    const handleAppMessage = (data) => {

      if (data.action === "create") {

        if (String(data.message.ticketId) !== String(currentTicketId.current)) {
          return;
        }

        dispatch({ type: "ADD_MESSAGE", payload: data.message });

        if (data.message.isNote) checkNotes();
        if (data.message.fromMe || isScrolledToBottom()) scrollToBottom();
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    };

    socket.on("appMessage", handleAppMessage);
    return () => {
      socket.off("connect", joinRoom);
      socket.off("appMessage", handleAppMessage);
    };
  }, [ticketId, checkNotes]);

  React.useLayoutEffect(() => {
    if (shouldScrollRef.current && listRef.current) {
        const newScrollHeight = listRef.current.scrollHeight;
        const diff = newScrollHeight - prevScrollHeight.current;
        
        listRef.current.scrollTop += diff;
        
        shouldScrollRef.current = false;
    }
  }, [messagesList]);

  useEffect(() => {
    recordDiagnosticEvent({
      eventType: "messages_list_state",
      component: "MessagesList",
      ticketIdHash: hashDiagnosticId(ticketId),
      counts: {
        messagesRendered: messagesList.length,
        pageNumber,
        hasMore: hasMore ? 1 : 0,
        loading: loading ? 1 : 0,
        notesCount
      }
    });
  }, [ticketId, messagesList.length, pageNumber, hasMore, loading, notesCount]);
  
  useEffect(() => {
    if (!highlightedMessageId || isHighlightedScrolled.current) {
      return;
    }

    const doScrollAndHighlight = (messageId) => {
      const element = document.getElementById(`message-${messageId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.style.transition = 'background-color 0.5s ease';
        element.style.backgroundColor = 'rgba(255, 215, 0, 0.5)';
        setTimeout(() => {
          if (element) element.style.backgroundColor = '';
        }, 3500);
        isHighlightedScrolled.current = true;
        return true;
      }
      return false;
    };

    const findAndScroll = async () => {
      const messageExists = messagesList.some(msg => msg.id === highlightedMessageId);

      if (messageExists) {
        doScrollAndHighlight(highlightedMessageId);
        return;
      }

      if (!isHighlightedScrolled.current && hasMore && !loading) {
        if (findMessageAttempts.current < 10) {
          if (listRef.current) {
            listRef.current.scrollTo({ top: 0, behavior: 'smooth' }); 
          }
          
          findMessageAttempts.current++;
          setPageNumber(prev => prev + 1);
        } else {
          toastError(new Error("No se pudo encontrar el mensaje especificado."));
          isHighlightedScrolled.current = true; 
        }
      } else if (!hasMore && !messageExists) {
        if (findMessageAttempts.current > 0) {
            toastError(new Error("No se pudo encontrar el mensaje. Puede que haya sido eliminado."));
            findMessageAttempts.current = 0;
            isHighlightedScrolled.current = true;
        }
      }
    };

    const initialSearchDelay = setTimeout(findAndScroll, 500);
    return () => clearTimeout(initialSearchDelay);

  }, [highlightedMessageId, messagesList, hasMore, loading, ticketId]);
  
  const handleSelectNote = (messageId) => {
    if (messageId) {
      isHighlightedScrolled.current = false;
      findMessageAttempts.current = 0;
      
      history.push(`/tickets/${ticketId}`);
      
      setTimeout(() => {
        history.push(`/tickets/${ticketId}?messageId=${messageId}`);
      }, 10);
    }
  };
  
  const loadMore = () => {
    if (highlightedMessageId && findMessageAttempts.current > 0) return;
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };
  
  const isScrolledToBottom = (tolerance = 100) => {
    const messagesListDiv = document.getElementById("messagesList");
    if (!messagesListDiv) return false;

    const { scrollHeight, scrollTop, clientHeight } = messagesListDiv;
    
    return scrollHeight - scrollTop - clientHeight < tolerance;
  };
  
  const handleScroll = (e) => {
    if (!e.currentTarget) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    clearTimeout(scrollDebounce.current);
    scrollDebounce.current = setTimeout(() => {
        if (scrollHeight - scrollTop < clientHeight + 250) {
            setShowScrollButton(false);
        } else {
            setShowScrollButton(true);
        }
    }, 150);

    if (!hasMore || loading) return;

    if (scrollTop < 50) {
      loadMore();
    }
  };
  
  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const formatDate = (dateString) => {
    const date = parseISO(dateString);
    const now = new Date();
    if (isSameDay(date, now)) {
      return "Hoy";
    }
    if (isYesterday(date, now)) {
      return "Ayer";
    }
    return format(date, "dd/MM/yyyy", { locale: es });
  };
  
  const initialScrollDone = useRef(false);
  
  useEffect(() => {
    if (messagesList.length > 0 && !initialScrollDone.current && !highlightedMessageId) {
      scrollToBottom();
      initialScrollDone.current = true;
    }
  }, [messagesList, highlightedMessageId]); 
  
  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else if (message.mediaType === "vcard") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />
    }
    /*else if (message.mediaType === "multi_vcard") {
    	
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
    else if (/^.*\.(jpe?g|png|gif|webp)$/i.test(message.mediaUrl) && message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio") {
      return <Audio url={message.mediaUrl} fromMe={message.fromMe} />
    } else if (message.mediaType === "video" && !message.mediaUrl.toLowerCase().endsWith(".mpeg")) {
      return (
        <video
          className={classes.messageMedia}
          src={message.mediaUrl}
          controls
        />
      );
    } else if (message.mediaType === "application") {
      return <DocumentMessage message={message} />;
    } else {
      const fullUrl = message.mediaUrl || "";
      const fileName = fullUrl.split("/").pop() || "Archivo desconocido";
      const fileType = message.mediaType || "Tipo desconocido";

      return (
        <>
          <div className={classes.downloadMedia}>
            <div className={classes.documentPreview}>
              <div className={classes.fileIcon}>📄</div>
              <div className={classes.fileName}>{fileName}</div>
              <div className={classes.fileType}>Tipo: {fileType}</div>

              <Button
                startIcon={<GetApp />}
                color="primary"
                variant="outlined"
                target="_blank"
                href={message.mediaUrl}
                className={classes.downloadButton}
              >
                Descargar
              </Button>
            </div>
          </div>
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {formatDate(message.createdAt)}
          </div>
        </span>
      );
    }

    const messageDay = parseISO(message.createdAt);
    const previousMessageDay = parseISO(messagesList[index - 1].createdAt);

    if (!isSameDay(messageDay, previousMessageDay)) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {formatDate(message.createdAt)}
          </div>
        </span>
      );
    }
    
    return null;
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const handleQuotedClick = (quotedMsgId) => {
    if (quotedMsgId) {
      isHighlightedScrolled.current = false;
      findMessageAttempts.current = 0;

      history.push(`/tickets/${ticketId}`);
      
      setTimeout(() => {
        history.push(`/tickets/${ticketId}?messageId=${quotedMsgId}`);
      }, 10);
    }
  };

  const escapeHtml = (unsafe) =>
    unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const renderQuotedMessage = (message) => {
    const quoted = message.quotedMsg;
    if (!quoted) return null;

    let body = quoted.body || "";

    if (quoted.mediaType === "audio") {
      body = "🎤 Mensaje de voz";
    }

    if (quoted.mediaType === "location") {
      const parts = quoted.body.split('|');
      
      if (parts.length >= 2) {
          const description = parts[2] || parts[1] || "Ubicación";
          
          if (description.includes("maps.google") || description.includes("http")) {
             body = "📍 Ubicación";
          } else {
             body = "📍 " + description;
          }
      } else {
          body = "📍 Ubicación";
      }
    }

    let safe = escapeHtml(body);

    const firmaRegex = /^\*(.+?)\*:?[\s\r\n]*/;

    if (firmaRegex.test(safe)) {
      safe = safe.replace(firmaRegex, (_m, p1) => `<strong>${p1.trim()}</strong> `);
    }

    return (
      <div
        style={{ cursor: "pointer" }}
        onClick={() => handleQuotedClick(quoted.id)}
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: quoted.fromMe,
          })}
        />
        <div className={classes.quotedMsg}>
          {!quoted.fromMe && (
            <span className={classes.messageContactName}>
              {quoted.contact?.name}
            </span>
          )}
          <span dangerouslySetInnerHTML={{ __html: safe }} />
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        if (message.isNote) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageNote} id={`message-${message.id}`}>
                <span className={classes.noteTitle}>NOTA INTERNA</span>
                <div className={classes.noteContentItem}> 
                  <MarkdownWrapper>{message.body}</MarkdownWrapper>
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
              </div>
              {index === messagesList.length - 1 && (
                <div ref={lastMessageRef} />
              )}
            </React.Fragment>
          );
        } else if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageLeft} id={`message-${message.id}`}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}
                {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                ) && checkMessageMedia(message)}
                <div className={classes.textContentItem}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  <MarkdownWrapper>{message.body}</MarkdownWrapper>
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
              </div>
              {index === messagesList.length - 1 && (
                <div ref={lastMessageRef} />
              )}
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageRight} id={`message-${message.id}`}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {!message.isDeleted && (message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.isDeleted && (
                    <>
                      <Block
                        color="disabled"
                        fontSize="small"
                        className={classes.deletedIcon}
                      />
                      <i className={classes.deletedText}>Mensaje eliminado.</i>
                    </>
                  )}
                  {!message.isDeleted && message.quotedMsg && renderQuotedMessage(message)}
                  {!message.isDeleted && <MarkdownWrapper>{message.body}</MarkdownWrapper>}
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                </div>
              </div>
              {index === messagesList.length - 1 && (
                <div ref={lastMessageRef} />
              )}
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      <Tooltip title={notesCount > 0 ? `Ver ${notesCount} Notas` : "Sin notas"} arrow>
        <span style={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}> 
          <IconButton
            onClick={() => notesCount > 0 && setDrawerOpen(true)}
            disabled={notesCount === 0}
            style={{ 
              backgroundColor: notesCount > 0 ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 255, 255, 0.7)',
            }}
          >
            <Comment />
          </IconButton>
        </span>
      </Tooltip>
      {showScrollButton && (
        <Tooltip title="Ir al último mensaje" arrow placement="left">
          <IconButton
            className={classes.scrollToBottomButton}
            onClick={scrollToBottom}
          >
            <ArrowDownward />
          </IconButton>
        </Tooltip>
      )}
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      <div
        id="messagesList"
        ref={listRef}
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
      <NotesDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ticketId={ticketId}
        onSelectNote={handleSelectNote}
      />
    </div>
  );
};

export default MessagesList;
